var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo } from 'react';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useRouter } from 'next/router';
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useAuthentication } from 'root/src/client/v2/auth/hooks/useAuthentication';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
// Redesign of the legacy UpdateEmailContactModal (tRPC users.confirmEmail). Both actions go
// through useAsyncAction for the in-button loader -> tick / X and the loading -> success /
// error toast (see docs/redesign.md). One hook per button so their states are independent;
// each disables the other while in flight.
export const RedesignUpdateEmailContactModal = memo(({ onConfirm, onCancel }) => {
    var _a, _b;
    const router = useRouter();
    const email = (_b = (_a = useAuthentication()) === null || _a === void 0 ? void 0 : _a.authentication) === null || _b === void 0 ? void 0 : _b.email;
    const confirmEmailMutation = trpc.users.confirmEmail.useMutation();
    const keep = useAsyncAction();
    const change = useAsyncAction();
    const busy = keep.loading || keep.success || change.loading || change.success;
    const onKeep = () => {
        void keep.run(() => confirmEmailMutation.mutateAsync(), {
            onSuccess: onConfirm,
            toast: {
                loading: 'Confirming email...',
                success: 'Email confirmed',
                error: caught => (caught instanceof Error ? caught.message : 'Could not confirm your email'),
            },
        });
    };
    const onChange = () => {
        void change.run(() => __awaiter(void 0, void 0, void 0, function* () {
            yield confirmEmailMutation.mutateAsync();
            yield router.push(routeUrls.changeEmail());
        }), {
            onSuccess: onCancel,
            successHold: 0,
            toast: {
                loading: 'Opening email change...',
                success: 'Redirecting to change your email',
                error: caught => (caught instanceof Error ? caught.message : 'Something went wrong'),
            },
        });
    };
    return (<RedesignModalShell className="sm:max-w-md">
			<div className="flex w-full flex-col items-center gap-3 text-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-neon-cyan/30 bg-neon-cyan/10">
					<MailOutlineIcon sx={{ fontSize: 24 }} className="text-neon-cyan"/>
				</div>
				<h2 className="font-display text-xl font-bold text-foreground">Email confirmation</h2>
				<p className="text-sm text-muted-foreground">Do you want to keep this email address?</p>
			</div>

			<div className="w-full">
				<span className="mb-1.5 block text-[11px] font-medium text-muted-foreground">Your current email address</span>
				<Input readOnly value={email !== null && email !== void 0 ? email : ''} className="w-full"/>
			</div>

			<div className="flex w-full gap-2">
				<Button variant="secondary" onClick={onChange} loading={change.loading} error={change.error} disabled={busy} className="flex-1 rounded-lg text-sm">
					Change my email
				</Button>
				<Button variant="brand" onClick={onKeep} loading={keep.loading} success={keep.success} error={keep.error} disabled={busy} className="flex-1 rounded-lg text-sm">
					Keep my email
				</Button>
			</div>
		</RedesignModalShell>);
});
// Promise-returning wrapper matching the legacy `displayUpdateContactInformationModal` contract, so
// the accept flow can await it. Stacks (`replace: false`) instead of replacing: this fires mid-submit
// with the key selection modal still open, and the legacy version rendered through the old modal
// context at a lower z-index, which left it visible under the redesign overlay but unclickable, so
// the accept hung on a promise that could never resolve.
export const useRedesignUpdateEmailContactModal = () => {
    const modal = useRedesignModal();
    return () => new Promise((resolve) => {
        modal.show(<RedesignUpdateEmailContactModal onConfirm={() => { modal.hide(); resolve(true); }} onCancel={() => { modal.hide(); resolve(false); }}/>, { replace: false, closeable: false });
    });
};
